import { z } from 'zod';

const vesselTypeVisibilityItemSchema = z.object({
  Id: z.string(),
  Name: z.string().optional()
});

export const vesselVisibilitySchema = z
  .object({
    VesselTypes: z.array(vesselTypeVisibilityItemSchema).optional().default([]),
    Fleets: z.array(z.unknown()).optional().default([]),
    ManagingCompanies: z.array(z.unknown()).optional().default([]),
    VesselClasses: z.array(z.unknown()).optional().default([]),
    Vessels: z.array(z.unknown()).optional().default([]),
    VesselFlags: z.array(z.unknown()).optional().default([])
  })
  .nullable()
  .optional();

const userVisibilitySchema = z
  .object({
    VesselVisibility: vesselVisibilitySchema
  })
  .nullable()
  .optional();

export type VesselTypeVisibilityItem = z.infer<typeof vesselTypeVisibilityItemSchema>;
export type UserVisibility = z.infer<typeof userVisibilitySchema>;

const roleSchema = z.object({
  Id: z.string(),
  Name: z.string(),
  MicroserviceId: z.string().nullable().optional(),
  UserRights: z.string().nullable().optional(),
  Permissions: z.array(z.string())
});

const departmentSchema = z.object({
  Id: z.string(),
  Name: z.string()
});

const companySchema = z.object({
  Id: z.string(),
  Name: z.string()
});

// Display-minimal, already-clamped vessel scope entry resolved at session time
// (HSQE-16 spec section 6.1). Provisional draft contract under ADR-0003 — additive
// and optional so non-HSQE whoami consumers are unaffected.
const vesselScopeEntrySchema = z.object({
  // Id stays required: it is the vessel identity (React key, selection, click target) —
  // an entry without it is unusable. Name/IMO are display fields and may be absent; a
  // shape mismatch can never break auth because getWhoAmI's safeParse is non-throwing.
  Id: z.string(),
  Name: z.string().optional(),
  IMO: z.string().optional(),
  VesselGroupId: z.string().nullable().optional(),
  VesselGroupName: z.string().nullable().optional()
});

const userSchema = z.object({
  Id: z.string(),
  Name: z.string(),
  FirstName: z.string(),
  LastName: z.string(),
  Email: z.string(),
  IsActive: z.boolean(),
  IsSystemUser: z.boolean(),
  DepartmentCode: z.string(),
  RankCode: z.string(),
  DutyRankId: z.string(),
  CurrentVesselId: z.string(),
  Roles: z.array(roleSchema),
  Departments: z.array(departmentSchema),
  Companies: z.array(companySchema),
  Positions: z.array(z.unknown()).optional(),
  Vessels: z.array(vesselScopeEntrySchema).optional(),
  VesselScopeKind: z.enum(['fleet-fixed', 'shore', 'shipboard']).optional(),
  Messages: z.array(z.unknown()).optional(),
  UserRights: z.string().nullable().optional(),
  Permissions: z.array(z.string()).optional(),
  Visibility: userVisibilitySchema
});

const siteInformationSchema = z.looseObject({
  ProductionCaution: z.boolean().optional()
});

export const whoAmISchema = z.object({
  UserName: z.string(),
  Security: z.object({
    UserName: z.string(),
    User: userSchema
  }),
  SiteInformation: siteInformationSchema.nullish()
});

export type Role = z.infer<typeof roleSchema>;
export type Department = z.infer<typeof departmentSchema>;
export type User = z.infer<typeof userSchema>;
export type VesselScopeEntry = z.infer<typeof vesselScopeEntrySchema>;
export type WhoAmIResponse = z.infer<typeof whoAmISchema>;
